import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useRef } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from './AuthContext';

interface WalletContextType {
  balance: number;
  loading: boolean;
  error: string | null;
  refreshBalance: () => void;
}

const WalletContext = createContext<WalletContextType>({
  balance: 0,
  loading: true,
  error: null,
  refreshBalance: () => {}
});

export const useWallet = () => useContext(WalletContext);

export function WalletProvider({ children }: { children: ReactNode }) {
  const { currentUser } = useAuth();
  const [balance, setBalance] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Use ref to store unsubscribe function
  const unsubscribeRef = useRef<(() => void) | null>(null);

  const setupWalletListener = useCallback(() => {
    if (!currentUser?.companyId) {
      setLoading(false);
      return;
    }

    try {
      // Clean up previous listener if exists
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }

      const docRef = doc(db, 'wallets', currentUser.companyId);
      unsubscribeRef.current = onSnapshot(
        docRef,
        (doc) => {
          if (doc.exists()) {
            const data = doc.data();
            setBalance(data.balance || 0);
            setError(null);
          } else {
            setBalance(0);
            setError(null);
          }
          setLoading(false);
        },
        (err) => {
          console.error('Error loading wallet:', err);
          setError('Erro ao carregar saldo da wallet');
          setLoading(false);
        }
      );
    } catch (err) {
      console.error('Error setting up wallet listener:', err);
      setError('Erro ao configurar listener da wallet');
      setLoading(false);
    }
  }, [currentUser?.companyId]);

  // Setup wallet listener when currentUser changes
  useEffect(() => {
    setupWalletListener();
    
    // Cleanup function
    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, [setupWalletListener]);

  const refreshBalance = useCallback(() => {
    setupWalletListener();
  }, [setupWalletListener]);

  const value = React.useMemo(() => ({
    balance,
    loading,
    error,
    refreshBalance
  }), [balance, loading, error, refreshBalance]);

  return (
    <WalletContext.Provider value={value}>
      {children}
    </WalletContext.Provider>
  );
}